import React from "react";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { utils, writeFile } from "xlsx";

interface DocumentosFaltantesChartProps {
  pieChartDocumentsData: any[];
}

const COLORS = [
  "#667eea",
  "#FF6B6B",
  "#50B7C0",
  "#F7B917",
  "#764ba2",
  "#FF8E53",
  "#50834C",
  "#f093fb",
  "#4facfe",
  "#43e97b",
];

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div style={styles.customTooltip}>
        <p style={styles.tooltipLabel}>{label}</p>
        {payload.map((entry: any, index: number) => (
          <p key={index} style={{ color: entry.color, margin: "4px 0" }}>
            {`${entry.name}: ${entry.value}`}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const DocumentosFaltantesChart: React.FC<DocumentosFaltantesChartProps> = ({
  pieChartDocumentsData,
}) => {
  const exportMissingDocuments = () => {
    const ws = utils.aoa_to_sheet([]);

    const headers = ["Documento", "Cantidad Faltante"];
    utils.sheet_add_aoa(ws, [headers], { origin: "A1" });

    let rowIndex = 2;
    pieChartDocumentsData.forEach((item) => {
      const row = [item.name, item.value];
      utils.sheet_add_aoa(ws, [row], { origin: `A${rowIndex}` });
      rowIndex++;
    });

    ws["!cols"] = [{ wch: 50 }, { wch: 20 }];

    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, "Documentos Faltantes");
    writeFile(wb, "Documentos_Mas_Faltantes.xlsx");
  };

  return (
    <div style={styles.chartCard}>
      <div style={styles.chartHeader}>
        <div style={styles.chartHeaderContent}>
          <h3 style={styles.chartTitle}>
            <i
              className="fa-solid fa-chart-pie"
              style={styles.chartTitleIcon}
            ></i>
            Documentos Más Faltantes
          </h3>
          <button
            onClick={exportMissingDocuments}
            style={styles.exportChartButton}
            title="Descargar Excel"
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "#16a34a";
              e.currentTarget.style.borderColor = "#16a34a";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "#22c55e";
              e.currentTarget.style.borderColor = "#22c55e";
            }}
          >
            <i
              className="fa-solid fa-file-arrow-down"
              style={styles.exportChartIcon}
            ></i>
            <span style={styles.exportChartText}>Excel</span>
          </button>
        </div>
      </div>
      <div style={styles.chartBody}>
        <ResponsiveContainer width="100%" height={350}>
          <PieChart>
            <Pie
              data={pieChartDocumentsData}
              cx="35%"
              cy="50%"
              labelLine={false}
              outerRadius={100}
              fill="#8884d8"
              dataKey="value"
            >
              {pieChartDocumentsData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={COLORS[index % COLORS.length]}
                />
              ))}
            </Pie>
            <Tooltip
              content={<CustomTooltip />}
              formatter={(value: number, name: string, props: any) => {
                const total = pieChartDocumentsData.reduce(
                  (sum, item) => sum + item.value,
                  0
                );
                const percent = ((value / total) * 100).toFixed(2);
                return [`${value} (${percent}%)`, name];
              }}
            />
            <Legend
              wrapperStyle={{
                paddingTop: "10px",
                paddingLeft: "10px",
                fontSize: "11px",
              }}
              iconType="circle"
              layout="vertical"
              verticalAlign="middle"
              align="right"
              width={280}
              formatter={(value) => {
                const item = pieChartDocumentsData.find(
                  (d) => d.name === value
                );
                const total = pieChartDocumentsData.reduce(
                  (sum, d) => sum + d.value,
                  0
                );
                const percent = item
                  ? ((item.value / total) * 100).toFixed(2)
                  : "0";
                const displayName =
                  value.length > 30 ? `${value.substring(0, 30)}...` : value;
                return `${displayName}: ${percent}%`;
              }}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  chartCard: {
    background: "#fff",
    borderRadius: "16px",
    boxShadow: "0 8px 24px rgba(0, 0, 0, 0.08)",
    overflow: "hidden",
    flex: 1,
  },
  chartHeader: {
    background: "linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)",
    padding: "20px 28px",
    borderBottom: "1px solid #e5e7eb",
  },
  chartHeaderContent: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
  },
  chartTitle: {
    margin: 0,
    fontSize: "18px",
    fontWeight: "600",
    color: "#2d3748",
    display: "flex",
    alignItems: "center",
    gap: "12px",
    flex: 1,
  },
  chartTitleIcon: {
    fontSize: "20px",
    color: "#667eea",
  },
  exportChartButton: {
    background: "#22c55e",
    border: "1px solid #22c55e",
    borderRadius: "8px",
    padding: "8px 14px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
    transition: "all 0.2s ease",
    minWidth: "auto",
  },
  exportChartIcon: {
    color: "#fff",
    fontSize: "14px",
    transition: "color 0.2s ease",
  },
  exportChartText: {
    color: "#fff",
    fontSize: "13px",
    fontWeight: "600",
    margin: 0,
  },
  chartBody: {
    padding: "28px",
  },
  customTooltip: {
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: "8px",
    padding: "12px",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
  },
  tooltipLabel: {
    fontWeight: "600",
    marginBottom: "8px",
    color: "#2d3748",
  },
};

export default DocumentosFaltantesChart;
